import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import CancelConditionField from 'components/Fields/CancelConditionField';
import { Stack } from '@koob/margaret';
import { KoobButton } from '@koob/ui';
import * as Yup from 'yup';
import { useState, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { getCancelConditionsSchema } from 'components/Fields/CancelConditionField/utils';
import CancelConditionsOverlapAlert from './CancelConditionsOverlapAlert';

// The `no-overlap` Yup test currently attaches its error to a specific
// condition's daysTo field (a nested field-error object), but a future test
// could attach it directly on the array item instead (a plain string) — and
// Object.values() on a string splits it into characters, so .includes()
// would silently never match. Handle both shapes so this can't go quiet.
export const hasOverlapError = (cancelConditionsErrors, overlapMessage) =>
  Array.isArray(cancelConditionsErrors) &&
  cancelConditionsErrors.some((itemErrors) => {
    if (!itemErrors) return false;
    if (typeof itemErrors === 'string') return itemErrors === overlapMessage;
    return Object.values(itemErrors).includes(overlapMessage);
  });

const CancellationConditionForm = ({
  cancelConditions,
  canUpdate,
  handleSubmit,
  currency,
  setIsUnsaved = () => {},
  timezone = 'UTC',
  showNumberOfNights = true,
  renderActions,
}) => {
  const { t } = useTranslation('hotelRoom');
  const [disabledSaveButton, setDisabledSaveButton] = useState(null);

  const CANCEL_CONDITION_SCHEMA = useMemo(
    () => getCancelConditionsSchema(showNumberOfNights),
    [showNumberOfNights],
  );

  return (
    <Formik
      initialValues={{
        cancelConditionsFormat: cancelConditions,
      }}
      validationSchema={Yup.object().shape({
        cancelConditionsFormat: CANCEL_CONDITION_SCHEMA,
      })}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange={true}
      validateOnBlur={true}
      validateOnMount={true}
    >
      {({ isSubmitting, dirty, handleReset, values, isValid, errors }) => {
        setIsUnsaved(dirty);
        const isConditionEmptied =
          cancelConditions !== values.cancelConditionsFormat &&
          isEmpty(values.cancelConditionsFormat);
        let isDisabled = true;
        if (dirty) {
          if (isValid || isConditionEmptied) {
            isDisabled = false;
          } else {
            setDisabledSaveButton(false);
          }
        }

        const showOverlapAlert = hasOverlapError(
          errors.cancelConditionsFormat,
          t('errors:cancelConditionsOverlap'),
        );

        return (
          <Form style={{ height: '100%' }}>
            {showOverlapAlert && <CancelConditionsOverlapAlert />}
            <CancelConditionField
              name="cancelConditionsFormat"
              setDisabledSaveButton={setDisabledSaveButton}
              timezone={timezone}
              showNumberOfNights={showNumberOfNights}
              currency={currency}
            />

            {canUpdate &&
              (renderActions ? (
                renderActions({
                  values,
                  dirty,
                  isSubmitting,
                  isValid,
                })
              ) : (
                <Stack
                  gutterSize={1}
                  alignX="flex-end"
                  alignY="center"
                  paddingTop={2}
                >
                  <KoobButton
                    type="button"
                    variant="secondary"
                    onClick={handleReset}
                  >
                    {t('misc:cancel')}
                  </KoobButton>
                  <KoobButton
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={isDisabled || disabledSaveButton}
                  >
                    {t('misc:save')}
                  </KoobButton>
                </Stack>
              ))}
          </Form>
        );
      }}
    </Formik>
  );
};

export default CancellationConditionForm;
